import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Target, TrendingUp, Shield, Heart, GraduationCap, Home, AlertTriangle, ArrowRight, Check } from 'lucide-react';
import { Link } from 'react-router-dom';

const GoalPlannerPage = () => {
  const benefits = [
    {
      icon: Target,
      title: "Clarity On What You're Saving For",
      description: "A vague goal leads to vague outcomes. Defining specific financial goals helps:",
      points: [
        "Set realistic savings targets",
        "Choose the right investment strategies",
        "Track progress and stay motivated over time"
      ]
    },
    {
      icon: TrendingUp,
      title: "Stay Ahead of Inflation",
      description: "If your money grows slower than inflation, you're losing wealth. Goal-based planning ensures:",
      points: [
        "Your savings stay relevant in future value terms",
        "Asset allocation keeps pace with market conditions",
        "Periodic reviews keep your plan adaptive and realistic"
      ]
    },
    {
      icon: Shield,
      title: "Peace of Mind During Life's Big Moments",
      description: "When it's time for your child's college, your retirement, or that long-awaited dream — the last thing you want is financial stress. A Goal Planner ensures:",
      points: [
        "You're financially ready, without scrambling or borrowing",
        "Stress-free transitions between life stages",
        "Confidence in every big life decision, backed by preparation"
      ]
    }
  ];

  const calculators = [
    {
      icon: Heart,
      title: "Marriage Planning",
      color: "from-pink-500 to-rose-500",
      features: ["Cost Estimate: Set a realistic budget", "Time Frame: Plan by their wedding age", "SIP Strategy: Invest monthly with ease"],
      link: "/calculators"
    },
    {
      icon: GraduationCap,
      title: "Education Planning",
      color: "from-blue-500 to-indigo-500",
      features: ["Tuition Costs: Estimate future expenses", "Timeline: Plan by admission year", "SIP Amount: Invest monthly to stay ready"],
      link: "/calculators"
    },
    {
      icon: Home,
      title: "House Planning",
      color: "from-emerald-500 to-teal-500",
      features: ["Budget Goal: Set your property target", "Time Frame: Choose your purchase year", "SIP Planning: Build your down payment"],
      link: "/calculators"
    },
    {
      icon: AlertTriangle,
      title: "Emergency Fund Builder",
      color: "from-orange-500 to-red-500",
      features: ["Fund Size: Set 6–12 months of expenses", "Access: Keep liquidity high and risk low", "Peace of Mind: Be prepared for any surprise"],
      link: "/calculators"
    }
  ];

  return (
    <div className="min-h-screen bg-black">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full border border-white/10 mb-6">
                <Target className="w-4 h-4 text-blue-400" />
                <span className="text-white/60 text-sm">Goal Planner</span>
              </div>

              <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Why Should You Have a Goal Planner?
              </h1>

              <p className="text-lg text-white/60 leading-relaxed mb-6">
                Big or small, everyone has life goals — some close, some far away. Whether it's building a fund for your child's education, planning a dream wedding, or preparing for a comfortable retirement, goals give structure to your finances and direction to your life.
              </p>

              <p className="text-white/80 mb-8">
                At <span className="text-blue-400 font-semibold">Frontier Investments</span>, our Goal Planner doesn't just help you save — it helps you save smartly, with purpose and discipline. With inflation, life stage changes, and market risks, a tailored and adaptive plan is your best bet to reach those milestones stress-free.
              </p>

              <Link
                to="/contact"
                className="inline-flex items-center gap-3 px-8 py-4 bg-white text-black font-semibold rounded-full hover:shadow-[0_0_40px_rgba(255,255,255,0.3)] transition-all duration-500 group"
              >
                Start Planning
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&h=600&fit=crop"
                alt="Goal Planning"
                className="rounded-3xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <div
                  key={index}
                  className="p-8 bg-gray-50 rounded-3xl border border-gray-100 hover:shadow-xl transition-all duration-500"
                >
                  <div className="p-4 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-2xl w-fit mb-6">
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-black mb-4">{benefit.title}</h3>
                  <p className="text-gray-600 mb-6">{benefit.description}</p>
                  <ul className="space-y-3">
                    {benefit.points.map((point, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <div className="p-1 bg-blue-100 rounded-full mt-1">
                          <Check className="w-3 h-3 text-blue-600" />
                        </div>
                        <span className="text-gray-600 text-sm">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Calculators Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Plan For Your Goals With Our Calculators
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Our goal planning calculators help you estimate investments needed for life's important milestones
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {calculators.map((calc, index) => {
              const IconComponent = calc.icon;
              return (
                <Link
                  key={index}
                  to={calc.link}
                  className="group p-6 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 hover:bg-white/10 transition-all duration-500"
                >
                  <div className={`p-3 bg-gradient-to-br ${calc.color} rounded-xl w-fit mb-4`}>
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-4">{calc.title}</h3>
                  <ul className="space-y-2">
                    {calc.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                        <span className="text-white/60 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-black mb-6">
            Ready to plan your financial goals?
          </h2>
          <p className="text-gray-500 mb-10">
            Let our experts help you create a personalized goal-based investment plan
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-3 px-8 py-4 bg-black text-white font-semibold rounded-full hover:shadow-lg transition-all duration-500 group"
          >
            Book a Consultation
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GoalPlannerPage;
